/**
 * NutSheetPrintView — print-only landscape rendering of Nut Sheet deal rows.
 *
 * Rendered hidden inside NutSheetPrintModal (#nut-sheet-print-container) and
 * made visible by the `printing-nut-sheet` @media print block in index.css.
 * Mirrors the on-screen table's 12 composite columns (stacked mini-lines in
 * the wide Deal Info / Trade Info cells) with a full-width date header row per
 * day. No summary cards or totals — deal rows only.
 *
 * Entries must arrive pre-filtered (applyRowFilters) and pre-sorted
 * (sortNutSheetEntries) so the printout matches the Excel Entries sheet.
 */

import React from 'react';
import {
  NutSheetEntry,
  isSwapRow,
  isWholesaleRow,
} from '../../types/nutSheet';
import {
  NutSheetExportFilters,
  composeVehicle,
  describeFilters,
} from '../../utils/nutSheetExport';
import { formatCurrency, formatCurrencySigned } from './formatters';

interface NutSheetPrintViewProps {
  locationName: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  /** Pre-filtered + pre-sorted rows to print. */
  entries: NutSheetEntry[];
  /** Total rows fetched for the range, before row filters — for "N of M". */
  totalFetched: number;
  excludeFiReserve: boolean;
  filters: NutSheetExportFilters;
}

/** "Tuesday, July 1, 2026" from YYYY-MM-DD, parsed as a local date. */
function formatDateLong(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "7/1/2026" from YYYY-MM-DD, parsed as a local date. */
function formatDateShort(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).toLocaleDateString('en-US');
}

function money(value: number | null): string {
  return value === null ? '' : formatCurrency(value);
}

/** Trade Retail is a dollar amount OR free text ("Auction") — render either. */
function moneyOrText(value: number | string | null): string {
  if (value === null) return '';
  return typeof value === 'number' ? formatCurrency(value) : value;
}

const COL_COUNT = 12;

/** Small gray label + value line inside a composite cell. */
const Line: React.FC<{ label?: string; italic?: boolean; children: React.ReactNode }> = ({
  label,
  italic,
  children,
}) => (
  <div className={italic ? 'italic text-gray-600' : undefined}>
    {label && <span className="text-gray-500">{label} </span>}
    {children}
  </div>
);

const TradeInfoCell: React.FC<{ entry: NutSheetEntry }> = ({ entry }) => {
  const trades: React.ReactNode[] = [];
  const t1Vehicle = composeVehicle(entry.tradeYear, entry.tradeMake, entry.tradeModel, entry.tradeMiles);
  if (entry.tradeStockNumber || t1Vehicle || entry.t1Plan || entry.tradeComment) {
    trades.push(
      <div key="t1">
        <Line label="T1:">{entry.tradeStockNumber ?? ''}</Line>
        {t1Vehicle && <Line>{t1Vehicle}</Line>}
        {entry.t1Plan && <Line label="Plan:">{entry.t1Plan}</Line>}
        {entry.tradeComment && <Line italic>{entry.tradeComment}</Line>}
      </div>,
    );
  }
  const t2Vehicle = composeVehicle(entry.trade2Year, entry.trade2Make, entry.trade2Model, entry.trade2Miles);
  if (entry.trade2StockNumber || t2Vehicle || entry.t2Plan || entry.trade2Comment) {
    trades.push(
      <div key="t2" className={trades.length > 0 ? 'mt-1' : undefined}>
        <Line label="T2:">{entry.trade2StockNumber ?? ''}</Line>
        {t2Vehicle && <Line>{t2Vehicle}</Line>}
        {entry.t2Plan && <Line label="Plan:">{entry.t2Plan}</Line>}
        {entry.trade2Comment && <Line italic>{entry.trade2Comment}</Line>}
      </div>,
    );
  }
  return <>{trades}</>;
};

const TradeMoneyCell: React.FC<{ entry: NutSheetEntry }> = ({ entry }) => {
  const blocks: React.ReactNode[] = [];
  if (entry.acv !== null || entry.retail !== null) {
    blocks.push(
      <div key="t1">
        {entry.acv !== null && <Line label="ACV">{formatCurrency(entry.acv)}</Line>}
        {entry.retail !== null && <Line label="Ret">{moneyOrText(entry.retail)}</Line>}
      </div>,
    );
  }
  if (entry.trade2Acv !== null || entry.trade2Retail !== null) {
    blocks.push(
      <div key="t2" className={blocks.length > 0 ? 'mt-1' : undefined}>
        {entry.trade2Acv !== null && <Line label="ACV">{formatCurrency(entry.trade2Acv)}</Line>}
        {entry.trade2Retail !== null && <Line label="Ret">{moneyOrText(entry.trade2Retail)}</Line>}
      </div>,
    );
  }
  return <>{blocks}</>;
};

const EntryRow: React.FC<{ entry: NutSheetEntry; stripe: boolean; excludeFiReserve: boolean }> = ({
  entry,
  stripe,
  excludeFiReserve,
}) => {
  const soldVehicle = composeVehicle(entry.soldYear, entry.soldMake, entry.soldModel);
  const hasAdj =
    (entry.estimatedGross !== null || entry.actualGross !== null) &&
    (entry.actualGross ?? 0) !== (entry.estimatedGross ?? 0);
  const netFi =
    entry.fAndI === null && entry.financeReserve === null
      ? null
      : (entry.fAndI ?? 0) - (entry.financeReserve ?? 0);
  const retail = !isWholesaleRow(entry) && !isSwapRow(entry);

  return (
    <tr className={stripe ? 'print-zebra' : undefined}>
      <td>
        <div className="font-semibold">{entry.category}</div>
        <div>{entry.type}</div>
      </td>
      <td>
        <div className="font-semibold">{entry.dealNumber ?? '—'}</div>
        {entry.stockNumber && <div className="text-gray-600">Stk {entry.stockNumber}</div>}
      </td>
      <td>
        {soldVehicle && <Line>{soldVehicle}</Line>}
        {entry.customerLastName && <Line label="Cust:">{entry.customerLastName}</Line>}
        {entry.salesperson && <Line label="SP:">{entry.salesperson}</Line>}
        {entry.salesperson2 && <Line label="SP2:">{entry.salesperson2}</Line>}
        {entry.salesManager && <Line label="Mgr:">{entry.salesManager}</Line>}
        {entry.fiRep && <Line label="F&I:">{entry.fiRep}</Line>}
        {entry.daysInStock !== null && <Line label="Days:">{entry.daysInStock}</Line>}
        {entry.openROs && <Line label="Open ROs:">{entry.openROs}</Line>}
        {entry.comment && <Line italic>{entry.comment}</Line>}
      </td>
      <td className="text-right">{money(entry.estimatedGross)}</td>
      <td className="text-right">
        <div>{money(entry.actualGross)}</div>
        {hasAdj && (
          <div className="text-gray-600">
            Adj {formatCurrencySigned((entry.actualGross ?? 0) - (entry.estimatedGross ?? 0))}
          </div>
        )}
        {entry.accountingComment && <div className="italic text-gray-600">{entry.accountingComment}</div>}
      </td>
      <td className="text-right">
        {excludeFiReserve ? (
          <>
            <div>{money(entry.fAndI)}</div>
            {entry.financeReserve !== null && (
              <div className="text-gray-600">Rsv {formatCurrency(entry.financeReserve)}</div>
            )}
            {netFi !== null && <div className="font-semibold">Net {formatCurrency(netFi)}</div>}
          </>
        ) : (
          money(entry.fAndI)
        )}
      </td>
      <td className="text-right">{money(entry.points)}</td>
      <td className="text-right">{money(entry.holdback)}</td>
      <td className="text-right">{money(entry.doc)}</td>
      <td className="text-center">
        {entry.financeSource && <div>{entry.financeSource}</div>}
        {retail && <div className="text-gray-600">CSI: {entry.csiBonus ? 'Y' : 'N'}</div>}
      </td>
      <td>
        <TradeInfoCell entry={entry} />
      </td>
      <td className="text-right">
        <TradeMoneyCell entry={entry} />
      </td>
    </tr>
  );
};

export const NutSheetPrintView: React.FC<NutSheetPrintViewProps> = ({
  locationName,
  startDate,
  endDate,
  entries,
  totalFetched,
  excludeFiReserve,
  filters,
}) => {
  // Group into date blocks for the header rows + block-level zebra striping
  // (same stripeOn/prevDate walk as the Excel Entries sheet).
  const countByDate = new Map<string, number>();
  entries.forEach((e) => countByDate.set(e.date, (countByDate.get(e.date) ?? 0) + 1));

  const rows: React.ReactNode[] = [];
  let stripeOn = false;
  let prevDate: string | null = null;
  entries.forEach((entry) => {
    if (entry.date !== prevDate) {
      stripeOn = prevDate === null ? false : !stripeOn;
      prevDate = entry.date;
      const dayCount = countByDate.get(entry.date) ?? 0;
      rows.push(
        <tr key={`date-${entry.date}`} className="print-date-row">
          <td colSpan={COL_COUNT}>
            {formatDateLong(entry.date)} — {dayCount} {dayCount === 1 ? 'deal' : 'deals'}
          </td>
        </tr>,
      );
    }
    rows.push(
      <EntryRow key={entry.entryId} entry={entry} stripe={stripeOn} excludeFiReserve={excludeFiReserve} />,
    );
  });

  return (
    <div className="text-[8pt] text-gray-900 bg-white">
      <div className="mb-2">
        <div className="text-base font-bold">Nut Sheet — {locationName}</div>
        <div className="text-gray-700">
          Date range: {formatDateShort(startDate)} to {formatDateShort(endDate)} · {entries.length} of{' '}
          {totalFetched} entries
        </div>
        <div className="italic text-gray-600">
          Printed {new Date().toLocaleString('en-US')} · F&amp;I Reserve:{' '}
          {excludeFiReserve ? 'Excluded (Net = F&I − Reserve)' : 'Included'} · Row filters:{' '}
          {describeFilters(filters)}
        </div>
      </div>
      <table className="w-full">
        <colgroup>
          <col style={{ width: '4%' }} /> {/* Type */}
          <col style={{ width: '6.5%' }} /> {/* Deal # / Stock # */}
          <col style={{ width: '19%' }} /> {/* Deal Info */}
          <col style={{ width: '6%' }} /> {/* Est. Gross */}
          <col style={{ width: '7%' }} /> {/* Actual Gross */}
          <col style={{ width: '6.5%' }} /> {/* F&I */}
          <col style={{ width: '6%' }} /> {/* Back End $ */}
          <col style={{ width: '6%' }} /> {/* Holdback */}
          <col style={{ width: '5%' }} /> {/* Doc */}
          <col style={{ width: '5.5%' }} /> {/* Fin Src / CSI */}
          <col style={{ width: '19%' }} /> {/* Trade Info */}
          <col style={{ width: '9.5%' }} /> {/* Trade $ */}
        </colgroup>
        <thead>
          <tr className="bg-gray-800 text-white">
            <th className="text-left">Type</th>
            <th className="text-left">Deal # / Stock #</th>
            <th className="text-left">Deal Info</th>
            <th className="text-right">Est. Gross</th>
            <th className="text-right">Actual Gross</th>
            <th className="text-right">F&amp;I</th>
            <th className="text-right">Back End $</th>
            <th className="text-right">Holdback</th>
            <th className="text-right">Doc</th>
            <th className="text-center">Fin Src / CSI</th>
            <th className="text-left">Trade Info</th>
            <th className="text-right">Trade $</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
};
